import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Star, MessageCircle, CreditCard, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const empty = { name: "", type: "subscription", price: "", interval: "monthly", whatsapp_quota: "", features: "", is_popular: false, active: true };

export default function AdminPlans() {
  const qc = useQueryClient();
  const { data: plans = [], isLoading } = useQuery({ queryKey: ["admin-plans"], queryFn: () => api.get("/admin/plans").then((r) => r.data) });
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, type: p.type, price: String(p.price), interval: p.interval,
      whatsapp_quota: String(p.whatsapp_quota), features: (p.features || []).join("\n"), is_popular: p.is_popular, active: p.active });
    setDialog(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name, type: form.type, price: parseFloat(form.price) || 0,
      interval: form.interval, whatsapp_quota: parseInt(form.whatsapp_quota) || 0,
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      is_popular: form.is_popular, active: form.active,
    };
    try {
      if (editing) await api.put(`/admin/plans/${editing.id}`, payload);
      else await api.post("/admin/plans", payload);
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      toast.success(editing ? "Plan güncellendi" : "Plan eklendi");
      setDialog(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/plans/${delId}`);
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      toast.success("Plan silindi");
    } catch (err) { toast.error(formatApiError(err)); }
    setDelId(null);
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Planlar</h1>
          <p className="text-muted-foreground mt-1">Abonelik planları ve WhatsApp ek paketleri</p>
        </div>
        <Button onClick={openNew} className="rounded-lg" data-testid="add-plan-btn"><Plus className="w-4 h-4 mr-1" /> Plan Ekle</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-8">
          {[
            { label: "Abonelik Planları", type: "subscription", per: (i) => (i === "monthly" ? "ay" : i === "yearly" ? "yıl" : "tek sefer") },
            { label: "WhatsApp Ek Paketleri", type: "whatsapp_addon", per: () => "tek sefer" },
          ].map((group) => {
            const items = plans.filter((p) => p.type === group.type);
            if (items.length === 0) return null;
            return (
              <div key={group.type}>
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">{group.label}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((p) => (
                    <div key={p.id} className={cn("rounded-2xl border bg-card p-5 flex flex-col", p.is_popular ? "border-primary" : "border-border")} data-testid="admin-plan-card">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {p.type === "whatsapp_addon" ? <MessageCircle className="w-4 h-4 text-emerald-600" /> : <CreditCard className="w-4 h-4 text-primary" />}
                          <h3 className="font-semibold">{p.name}</h3>
                        </div>
                        {p.is_popular && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                      </div>
                      <p className="mt-2"><span className="font-display text-2xl font-bold">{money(p.price)}</span>
                        <span className="text-muted-foreground text-sm"> / {group.per(p.interval)}</span></p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {p.whatsapp_quota} bildirim · <span className={p.active ? "text-emerald-600" : "text-red-600"}>{p.active ? "Aktif" : "Pasif"}</span>
                      </p>
                      {(p.features || []).length > 0 && (
                        <ul className="mt-3 space-y-1.5 flex-1">
                          {p.features.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                              <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => openEdit(p)} data-testid="edit-plan-btn"><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDelId(p.id)} data-testid="delete-plan-btn"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="plan-dialog">
          <DialogHeader><DialogTitle>{editing ? "Planı Düzenle" : "Yeni Plan"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label>Plan Adı</Label><Input value={form.name} onChange={set("name")} className="h-11 mt-1.5" data-testid="plan-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="plan-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subscription">Abonelik</SelectItem>
                    <SelectItem value="whatsapp_addon">WhatsApp Paketi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Periyot</Label>
                <Select value={form.interval} onValueChange={(v) => setForm((f) => ({ ...f, interval: v }))}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="plan-interval-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Aylık</SelectItem>
                    <SelectItem value="yearly">Yıllık</SelectItem>
                    <SelectItem value="once">Tek Sefer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fiyat (₺)</Label><Input type="number" value={form.price} onChange={set("price")} className="h-11 mt-1.5" data-testid="plan-price-input" /></div>
              <div><Label>WhatsApp kotası</Label><Input type="number" value={form.whatsapp_quota} onChange={set("whatsapp_quota")} className="h-11 mt-1.5" data-testid="plan-quota-input" /></div>
            </div>
            <div><Label>Özellikler (her satır bir madde)</Label><Textarea value={form.features} onChange={set("features")} rows={5} className="mt-1.5" data-testid="plan-features-input" /></div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_popular} onCheckedChange={(v) => setForm((f) => ({ ...f, is_popular: v }))} data-testid="plan-popular-toggle" /> Popüler</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="plan-active-toggle" /> Aktif</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>İptal</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.price} data-testid="save-plan-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Planı sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-plan">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
